// The over-the-board promotion picker, shared by the online (`game.ts`) and
// vs-computer (`computer.ts`) screens so they render identically. The choices
// reuse the board's own cburnett sprites: the card carries chessground's
// `.cg-wrap` class, so the `piece.<role>.<color>` background-image rules from
// `chessground.cburnett.css` resolve — which means the pieces always show in the
// promoting side's colour, matching the board exactly (the old Unicode-glyph
// version was hard-coded to one colour). See the `.promotion` block in
// `style.css` for the frosted-glass styling that sits over the (dimmed) board.

import type { Color } from "../game/types.ts";
import { t } from "../i18n/index.ts";

// role class (chessground) → UCI promotion letter, in the usual picker order.
const ROLES: readonly [role: string, code: string][] = [
  ["queen", "q"],
  ["rook", "r"],
  ["bishop", "b"],
  ["knight", "n"],
];

/**
 * Build the promotion card for the given `color`. `onPick(code)` fires with the
 * UCI letter for the chosen piece, or `null` if the player cancels; the caller
 * is responsible for hiding the overlay.
 */
export function promotionCard(color: Color, onPick: (code: string | null) => void): HTMLElement {
  const card = document.createElement("div");
  card.className = "promo-card cg-wrap"; // `cg-wrap` scopes the cburnett sprites

  const choices = document.createElement("div");
  choices.className = "promo-choices";
  for (const [role, code] of ROLES) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "promo-piece";
    btn.setAttribute("aria-label", t("promo.promoteTo", { role: t(`promo.${role}`) }));
    const piece = document.createElement("piece");
    piece.className = `${role} ${color}`;
    btn.append(piece);
    btn.onclick = () => onPick(code);
    choices.append(btn);
  }
  card.append(choices);

  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "btn ghost promo-cancel";
  cancel.textContent = t("promo.cancel");
  cancel.onclick = () => onPick(null);
  card.append(cancel);

  return card;
}
